import React, { useState } from "react";
import axios from "axios";
import config from "../../../config";
import { Modal } from "react-bootstrap";
import { toast } from "../../common/Toast";
import { setUser } from "../../../features/user/userSlice";
import { useSelector, useDispatch } from "react-redux";
const AccountSetting = ({ userInfo, token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newEmail, setNewEmail] = useState(userInfo?.email);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [emailOTP, setEmailOTP] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const dispatch = useDispatch()

  // Change password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Show/hide toggles for each password field
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Simple inline eye / eye-slash icon button, no external icon library needed
  const EyeToggleButton = ({ visible, onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      tabIndex={-1}
      style={{
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: "#6c757d",
        display: "flex",
        alignItems: "center",
      }}
    >
      {visible ? (
        // eye-slash icon (currently visible -> click to hide)
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        // eye icon (currently hidden -> click to show)
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleEmailChange = (e) => {
    setNewEmail(e.target.value);
  };

  const sendOTP = async () => {
    try {
      setLoading(true)
      await axios.post(`${config.baseURL}/api/auth/send-email-otp`, {
        email: newEmail
      });
      setCountdown(60);
      setShowOTPModal(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOTPAndUpdateEmail = async () => {
    try {
      setLoading(true);
      await axios.post(`${config.baseURL}/api/auth/verify-email-otp`, {
        email: newEmail,
        otp: emailOTP
      });
      // If OTP is verified, update the email
      const res = await axios.put(`${config.baseURL}/api/profile/update-email`, {
        newEmail,
        userId: userInfo?.id
      }, {
        headers: {
          Authorization: `Bearer ${token}` // or however you handle auth
        }
      });
      if (res.data.success) {
        setShowOTPModal(false)
        toast.success("Email updated succssfully")
        const updatedUser = {
          ...userInfo,
          email: res.data.email,
        };
        dispatch(setUser({
          userInfo: updatedUser,
          token: token, // ← do NOT change token
        }));
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP or failed to update email. Please try again.");
      console.log("Update email error ", err)
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (countdown > 0) return;
    await sendOTP();
  };

  // Countdown effect
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const resetPasswordFormState = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password do not match.");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from your current password.");
      return;
    }

    try {
      setPasswordLoading(true);
      const res = await axios.put(
        `${config.baseURL}/api/profile/change-password`,
        { currentPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (res.data.success) {
        toast.success("Password changed successfully");
        resetPasswordFormState();
      }
    } catch (err) {
      const message = err.response?.data?.message || "Failed to change password. Please try again.";
      setPasswordError(message);
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
  <div className="accordion-item">
      <h2 className="accordion-header" id="headingOne">
        <button
          className={`accordion-button ${isOpen ? "" : "collapsed"}`}
          type="button"
          onClick={toggleAccordion}
        >
          Account Settings
        </button>
      </h2>

      {isOpen && (
        <div className="accordion-collapse collapse show">
          <div className="accordion-body">
            <div className="row">
              <div className="col-md-6 border-end pe-md-5">
                <div className="form-box">
                  <h5 className="mb-3 fw-bold">Update Email Id</h5>
                  {success && <div className="alert alert-success">{success}</div>}
                  {error && <div className="alert alert-danger">{error}</div>}
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    sendOTP();
                  }}>
                    <div className="mb-4">
                      <label className="form-label">New Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={newEmail}
                        onChange={handleEmailChange}
                        required
                      />
                    </div>
                    <div className="d-flex gap-3">
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => {
                          setNewEmail(userInfo?.email);
                          setError("");
                          setSuccess("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={newEmail === userInfo?.email || loading}
                      >
                        {loading ? "Sending OTP..." : "Update Email"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="col-md-6 ps-md-5">
                <div className="form-box">
                  <h5 className="mb-3 fw-bold">Change Password</h5>
                  {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}
                  {passwordError && <div className="alert alert-danger">{passwordError}</div>}
                  <form onSubmit={handleChangePassword}>
                    <div className="mb-3">
                      <label className="form-label">Current Password</label>
                      <div className="position-relative">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          className="form-control"
                          style={{ paddingRight: "40px" }}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          autoComplete="current-password"
                          required
                        />
                        <EyeToggleButton
                          visible={showCurrentPassword}
                          onClick={() => setShowCurrentPassword((v) => !v)}
                          label={showCurrentPassword ? "Hide current password" : "Show current password"}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">New Password</label>
                      <div className="position-relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className="form-control"
                          style={{ paddingRight: "40px" }}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          autoComplete="new-password"
                          minLength={6}
                          required
                        />
                        <EyeToggleButton
                          visible={showNewPassword}
                          onClick={() => setShowNewPassword((v) => !v)}
                          label={showNewPassword ? "Hide new password" : "Show new password"}
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="form-label">Confirm New Password</label>
                      <div className="position-relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          style={{ paddingRight: "40px" }}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          autoComplete="new-password"
                          minLength={6}
                          required
                        />
                        <EyeToggleButton
                          visible={showConfirmPassword}
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={resetPasswordFormState}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={
                          passwordLoading ||
                          !currentPassword ||
                          !newPassword ||
                          !confirmPassword
                        }
                      >
                        {passwordLoading ? "Updating..." : "Change Password"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showOTPModal}
        onHide={!showOTPModal}
        backdrop="static"
        keyboard={false}
        centered
        size="lg"
        className="letsbeginmodal"
      >
        <Modal.Header>
          <h5 className="modal-title">Verify Email</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowOTPModal(false)}
          ></button>
        </Modal.Header>
        <Modal.Body>

          <p>We've sent a 4-digit OTP to {newEmail}</p>
          <div className="mb-3">
            <input
              type="text"
              className="form-control text-center otp-input"
              value={emailOTP}
              onChange={(e) => setEmailOTP(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="----"
              maxLength={4}
            />
          </div>
          {error && <div className="alert alert-danger">{error}</div>}


          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowOTPModal(false);
                setEmailOTP("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn py-2 btn-filled mb-2"
              onClick={verifyOTPAndUpdateEmail}
              disabled={emailOTP.length !== 4 || loading}
            >
              {loading ? "Verifying..." : "Verify & Update"}
            </button>
            <div className="text-center mb-3">
              <a
                href="#"
                className="text-primary text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  resendOTP();
                }}
                style={{ opacity: countdown > 0 ? 0.5 : 1 }}
              >
                Resend OTP {countdown > 0 ? `(${countdown}s)` : ""}
              </a>
            </div>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AccountSetting;